/**
 * Maintenance — one-shot admin operations.
 *
 * Currently:
 *  - `backfillMemberOrgs` — populate `users/{uid}.memberOrgs` for
 *    every existing user/org pair. Required ONCE after the
 *    `onOrganizationWriteMemberOrgs` trigger ships so the fast path
 *    in `computeUserClaims` covers every user (not just users whose
 *    org membership was touched after the deploy).
 *
 * Future one-shots (schema migrations, mass updates) drop in here.
 */
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDatabaseExport, IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';

import { callAdmin } from '@/lib/firebase';

type BackfillResult = {
  scannedOrgs: number;
  affectedUsers: number;
  writes: number;
  sampleUserIds: string[];
};

export function MaintenanceRoute() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BackfillResult | null>(null);

  const runBackfill = async () => {
    if (
      !window.confirm(
        'Run memberOrgs backfill?\n\n' +
          'This scans every org and writes the memberOrgs field on every ' +
          'affected user doc. Safe to re-run. Takes 30-60 seconds for ~1000 orgs.',
      )
    )
      return;

    setRunning(true);
    setResult(null);
    try {
      const { data } = await callAdmin.backfillMemberOrgs({});
      const r: BackfillResult = {
        scannedOrgs: data.scannedOrgs,
        affectedUsers: data.affectedUsers,
        writes: data.writes,
        sampleUserIds: data.sampleUserIds,
      };
      setResult(r);
      notifications.show({
        color: 'green',
        title: 'Backfill complete',
        message: `Scanned ${r.scannedOrgs} orgs, wrote memberOrgs on ${r.affectedUsers} users.`,
      });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Backfill failed',
        message: (e as Error).message ?? 'Unknown error',
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Stack gap="lg">
      <div>
        <Title order={2}>Maintenance</Title>
        <Text c="dimmed" size="sm">
          One-shot operations. Most are idempotent (safe to re-run).
        </Text>
      </div>

      <Card withBorder radius="md" padding="lg">
        <Group justify="space-between" mb="xs" align="flex-start">
          <div>
            <Group gap={6}>
              <IconDatabaseExport size={18} />
              <Title order={4}>Backfill memberOrgs</Title>
            </Group>
            <Text c="dimmed" size="sm" mt={4}>
              Populate <Code>users/{'{uid}'}.memberOrgs</Code> for every
              existing user/org pair. Required once after the trigger ships;
              speeds up org switching from ~4-8s to ~1s.
            </Text>
          </div>
          <Badge color={result ? 'green' : 'gray'} variant="light">
            {result ? 'Last run OK' : 'Never run'}
          </Badge>
        </Group>

        <Alert color="orange" icon={<IconAlertTriangle size={16} />} mt="sm">
          Idempotent — safe to re-run. Run ONCE right after deploying the
          new trigger. Subsequent updates are handled automatically.
        </Alert>

        {result ? (
          <Card withBorder radius="sm" padding="md" mt="md" bg="gray.0">
            <Text size="sm" fw={600} mb={6}>
              Last result
            </Text>
            <Text size="sm">
              • Scanned <b>{result.scannedOrgs}</b> orgs
            </Text>
            <Text size="sm">
              • Wrote memberOrgs on <b>{result.affectedUsers}</b> users
            </Text>
            <Text size="sm">
              • Total write ops: <b>{result.writes}</b>
            </Text>
            {result.sampleUserIds.length > 0 ? (
              <Text size="xs" c="dimmed" mt={6}>
                Sample uids: {result.sampleUserIds.slice(0, 5).join(', ')}…
              </Text>
            ) : null}
          </Card>
        ) : null}

        <Group justify="flex-end" mt="md">
          <Button
            color="blue"
            leftSection={<IconDatabaseExport size={14} />}
            loading={running}
            onClick={runBackfill}
          >
            Run backfill
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}
